import { zodResolver } from '@hookform/resolvers/zod';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { parseISO } from 'date-fns';
import { useEffect, useRef } from 'react';
import ReactDatePicker from 'react-datepicker';
import { Controller, useForm, UseFormReturn } from 'react-hook-form';
import { toast } from 'react-toastify';
import { z } from 'zod';
import type { Document, OwnerTypeEnum } from '../../../../../js/api';
import { DocumentsService, StatusEnum } from '../../../../../js/api';
import { handleApiError } from '../../../../../js/app/utils/errorhandling';
import { Button } from '../../../components/button';
import { Checkbox, CheckboxField } from '../../../components/checkbox';
import { Dialog, DialogActions, DialogBody, DialogTitle } from '../../../components/dialog';
import { DOCUMENTS_QUERY_KEY } from '../../../components/documents-tab';
import { DropdownSelect } from '../../../components/dropdown-select';
import { FieldValidationErrorMessage } from '../../../components/field-validation-error-message';
import { Description, Field, Fieldset, Label } from '../../../components/fieldset';
import { Input } from '../../../components/input';
import { WorkflowScopes } from '../../../constants/workflows';
import { useCodeDefinitionsAsOptions } from '../../../hooks/useCodeDefinitions';
import { parseFormDate } from '../../../utils/date';
import { uploadDocument } from '../../../utils/uploadDocument';
import { useAudience } from '../../../utils/workflowAccess';
import {
  credentialApi,
  CredentialKind,
  CredentialRow,
  CredentialSectionSpec,
  CredentialWrite,
  isBoardRow,
  isEcfmgRow,
  isImmunizationRow,
  isLicenseRow,
  STAFF_PROVIDER_CREDENTIALS_QUERY_KEY,
} from './credentials';
import { STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY, STAFF_PROVIDER_QUERY_KEY } from './queryKeys';
import 'react-datepicker/dist/react-datepicker.css';

const baseSchema = z.object({
  // the board / life-support type; license and immunization sections fix the type
  type: z.string().optional(),
  state: z.string().optional(),
  number: z.string().trim().max(50).optional(),
  status: z.string().optional(),
  board_eligible: z.boolean().optional(),
  exempt: z.boolean().optional(),
  issue_date: z.date().nullable().optional(),
  expiration_date: z.date().nullable().optional(),
  file: z.instanceof(File).optional(),
});
const schema = baseSchema.refine(
  (values) =>
    !values.issue_date || !values.expiration_date || values.expiration_date > values.issue_date,
  { message: 'Expiration date must be after the issue date', path: ['expiration_date'] }
);
type FormValues = z.infer<typeof schema>;

const toDate = (value: string | null | undefined): Date | null => (value ? parseISO(value) : null);

/** Form values for an existing entry (or the blanks for a new one). */
const initialValues = (row: CredentialRow | null | undefined): FormValues => ({
  type: row && !isLicenseRow(row) && !isImmunizationRow(row) && !isEcfmgRow(row) ? row.type : '',
  state: row && isLicenseRow(row) ? row.state : '',
  number: row && (isLicenseRow(row) || isEcfmgRow(row)) ? row.number : '',
  status: row && isLicenseRow(row) ? row.status : StatusEnum.ACTIVE,
  board_eligible: row && isBoardRow(row) ? row.board_eligible : false,
  exempt: row && isImmunizationRow(row) ? row.exempt : false,
  issue_date: toDate(row?.issue_date),
  expiration_date: toDate(row?.expiration_date),
  file: undefined,
});

/** API field errors mapped onto form fields (`document_id` → the file field). */
const fieldErrorsFrom = (error: unknown): Array<[keyof FormValues, string]> => {
  const body = (error as { body?: Record<string, unknown> })?.body;
  if (!body || typeof body !== 'object') return [];
  return Object.entries(body).flatMap(([field, messages]) => {
    const name = field === 'document_id' ? 'file' : field;
    if (!(name in baseSchema.shape)) return [];
    const message = Array.isArray(messages) ? String(messages[0]) : String(messages);
    return [[name as keyof FormValues, message]];
  });
};

/** The required pick the schema cannot see (it depends on the section), if missing. */
const requiredPickError = (
  section: CredentialSectionSpec,
  values: FormValues
): [keyof FormValues, string] | null => {
  if (section.kind === 'license' && !values.state) return ['state', 'State is required'];
  if (section.kind === 'board' && !values.type) return ['type', 'Board is required'];
  if (section.kind === 'life' && !values.type) return ['type', 'Type is required'];
  return null;
};

type FieldsProps = { form: UseFormReturn<FormValues>; isPending: boolean };

/** State (required), status and number — a license entry's own fields. */
function LicenseFields({ form, isPending }: FieldsProps) {
  const { register, setValue, watch, formState } = form;
  const { data: stateOptions, isLoading: statesLoading } =
    useCodeDefinitionsAsOptions('LicenseStateChoices');
  const { data: statusOptions } = useCodeDefinitionsAsOptions('LicenseStatus');
  return (
    <>
      <Field>
        <Label>
          State <span className="tw:text-stoplight-danger">*</span>
        </Label>
        <DropdownSelect
          allOptions={stateOptions ?? []}
          value={watch('state') || null}
          onChange={(option) => {
            const next = Array.isArray(option) ? (option[0] ?? null) : option;
            setValue('state', next ? next.value : '', { shouldValidate: true });
          }}
          isMulti={false}
          isDisabled={statesLoading || isPending}
          placeholder={statesLoading ? 'Loading states...' : 'Select a state'}
        />
        {formState.errors.state && (
          <FieldValidationErrorMessage message={formState.errors.state.message} />
        )}
      </Field>
      <Field>
        <Label>Status</Label>
        <DropdownSelect
          allOptions={statusOptions ?? []}
          value={watch('status') || null}
          onChange={(option) => {
            const next = Array.isArray(option) ? (option[0] ?? null) : option;
            setValue('status', next ? next.value : StatusEnum.ACTIVE);
          }}
          isMulti={false}
          isDisabled={isPending}
        />
      </Field>
      {/* selects pair with selects and inputs with inputs: the react-select control is
          taller than a text / date input, so mixing them in one row leaves the controls
          at different heights */}
      <Field className="tw:sm:col-span-2">
        <Label>License Number</Label>
        <Input
          {...register('number')}
          disabled={isPending}
          invalid={!!formState.errors.number}
          data-testid="credential-number"
        />
        {formState.errors.number && (
          <FieldValidationErrorMessage message={formState.errors.number.message} />
        )}
      </Field>
    </>
  );
}

/** The board / life-support type picker (required). */
function TypePickerField({ form, isPending, kind }: FieldsProps & { kind: 'board' | 'life' }) {
  const { setValue, watch, formState } = form;
  const { data: options, isLoading } = useCodeDefinitionsAsOptions(
    kind === 'board' ? 'BoardCertificationType' : 'CertificationType'
  );
  return (
    <Field className="tw:sm:col-span-2">
      <Label>
        {kind === 'board' ? 'Board' : 'Type'} <span className="tw:text-stoplight-danger">*</span>
      </Label>
      <DropdownSelect
        allOptions={options ?? []}
        value={watch('type') || null}
        onChange={(option) => {
          const next = Array.isArray(option) ? (option[0] ?? null) : option;
          setValue('type', next ? next.value : '', { shouldValidate: true });
        }}
        isMulti={false}
        isDisabled={isLoading || isPending}
        placeholder={isLoading ? 'Loading...' : 'Select one'}
      />
      {formState.errors.type && (
        <FieldValidationErrorMessage message={formState.errors.type.message} />
      )}
    </Field>
  );
}

/** A boolean flag of the entry (board eligible / exempt). */
function FlagCheckbox({
  form,
  isPending,
  name,
  label,
  testId,
}: FieldsProps & { name: 'board_eligible' | 'exempt'; label: string; testId: string }) {
  return (
    <Field className="tw:sm:col-span-2">
      <Controller
        name={name}
        control={form.control}
        render={({ field }) => (
          <CheckboxField>
            <Checkbox
              color="brandgreen1"
              checked={field.value ?? false}
              onChange={field.onChange}
              disabled={isPending}
              data-testid={testId}
            />
            <Label>{label}</Label>
          </CheckboxField>
        )}
      />
    </Field>
  );
}

/** The certificate number — an ECFMG entry's own field (optional: keyed in as found). */
function EcfmgFields({ form, isPending }: FieldsProps) {
  const { register, formState } = form;
  return (
    <Field className="tw:sm:col-span-2">
      <Label>Certificate Number</Label>
      <Input
        {...register('number')}
        disabled={isPending}
        invalid={!!formState.errors.number}
        data-testid="credential-number"
      />
      {formState.errors.number && (
        <FieldValidationErrorMessage message={formState.errors.number.message} />
      )}
    </Field>
  );
}

/** The section's own fields, above the shared dates and file. */
function SectionFields({ form, isPending, kind }: FieldsProps & { kind: CredentialKind }) {
  switch (kind) {
    case 'license':
      return <LicenseFields form={form} isPending={isPending} />;
    case 'ecfmg':
      return <EcfmgFields form={form} isPending={isPending} />;
    case 'board':
      return (
        <>
          <TypePickerField form={form} isPending={isPending} kind="board" />
          <FlagCheckbox
            form={form}
            isPending={isPending}
            name="board_eligible"
            label="Board eligible (not yet certified)"
            testId="credential-board-eligible"
          />
        </>
      );
    case 'life':
      return <TypePickerField form={form} isPending={isPending} kind="life" />;
    default:
      return (
        <FlagCheckbox
          form={form}
          isPending={isPending}
          name="exempt"
          label="Exempt (the document is the exemption letter)"
          testId="credential-exempt"
        />
      );
  }
}

/** The entry's write body for its section: typed sections fix `type` from the section. */
const toBody = (section: CredentialSectionSpec, values: FormValues): Partial<CredentialWrite> => {
  const body: Partial<CredentialWrite> = {
    issue_date: values.issue_date ? parseFormDate(values.issue_date) : null,
    expiration_date: values.expiration_date ? parseFormDate(values.expiration_date) : null,
  };
  switch (section.kind) {
    case 'license':
      return {
        ...body,
        type: section.typeCode,
        state: values.state,
        number: values.number || '',
        status: values.status || StatusEnum.ACTIVE,
      };
    case 'immunization':
      return { ...body, type: section.typeCode, exempt: Boolean(values.exempt) };
    case 'ecfmg':
      return { ...body, number: values.number || '' };
    case 'board':
      return { ...body, type: values.type, board_eligible: Boolean(values.board_eligible) };
    default:
      return { ...body, type: values.type };
  }
};

const FILE_INPUT_CLASSES =
  'tw:block tw:w-full tw:text-sm tw:text-zinc-700 tw:file:mr-3 tw:file:rounded-md tw:file:border-0 tw:file:bg-brand-green-2 tw:file:px-3 tw:file:py-1.5 tw:file:text-sm tw:file:font-medium tw:file:text-brand-green-0 tw:hover:file:bg-brand-green-1 tw:hover:file:text-white tw:disabled:opacity-50 tw:disabled:cursor-not-allowed';

type CredentialFormDialogProps = {
  open: boolean;
  onClose: () => void;
  providerId: string;
  section: CredentialSectionSpec;
  /** Editing an existing entry; omitted for a new one. */
  row?: CredentialRow | null;
};

/**
 * Add or edit a credential entry — the structured row (state / number / status for a
 * license, board + eligibility for a board certification, type for a life-support
 * certification, exemption for an immunization, certificate number for an ECFMG
 * certificate, plus issue and expiration dates)
 * together with its backing document.
 * Saving writes the row first (an entry without its image is still an entry, as on the
 * legacy profile), then uploads the file through the documents framework and links it.
 * Replacing an existing document is the paperclip's job, so the file field only shows
 * while the entry has none.
 */
export function CredentialFormDialog({
  open,
  onClose,
  providerId,
  section,
  row,
}: CredentialFormDialogProps) {
  const queryClient = useQueryClient();
  const audience = useAudience();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const isEdit = Boolean(row);
  const { kind } = section;

  const form = useForm<FormValues>({
    resolver: zodResolver(schema),
    defaultValues: { status: StatusEnum.ACTIVE, board_eligible: false, exempt: false },
  });
  const { control, handleSubmit, reset, setError, setValue, watch, formState } = form;

  useEffect(() => {
    if (!open) return;
    reset(initialValues(row));
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, [open, row, reset]);

  const invalidate = () => {
    queryClient.invalidateQueries({
      queryKey: [STAFF_PROVIDER_CREDENTIALS_QUERY_KEY, providerId],
    });
    // licensed states / IMLC summary on the header, documents tab + count
    queryClient.invalidateQueries({ queryKey: [STAFF_PROVIDER_QUERY_KEY, providerId] });
    queryClient.invalidateQueries({ queryKey: [DOCUMENTS_QUERY_KEY, providerId] });
    queryClient.invalidateQueries({
      queryKey: [STAFF_PROVIDER_DOCUMENTS_COUNT_QUERY_KEY, providerId],
    });
  };

  const saveMutation = useMutation({
    mutationFn: async (values: FormValues) => {
      const api = credentialApi[kind];
      const body = toBody(section, values);
      const saved = row
        ? await api.patch(row.id, body)
        : await api.create({ provider: providerId, ...body });
      if (!values.file) return { saved, uploadFailed: false };
      // the row is saved; the image is a second step so a failed upload never loses the entry
      let document: Document | null = null;
      try {
        document = await uploadDocument({
          file: values.file,
          documentTypeSlug: section.documentTypeSlug,
          ownerType: audience as OwnerTypeEnum,
          ownerId: providerId,
          contextType: WorkflowScopes.Provider,
          contextId: providerId,
          label: saved.title,
        });
        await api.patch(saved.id, { document_id: document.id });
        return { saved, uploadFailed: false };
      } catch (error) {
        if (document) {
          // uploaded but not linked: take the document back down so it is not left
          // as an orphan on the Documents tab (best effort — the warning covers the rest)
          await DocumentsService.documentsDestroy({ id: document.id }).catch(() => undefined);
        }
        handleApiError(error as Error, { messagePrefix: 'Document upload failed' });
        return { saved, uploadFailed: true };
      }
    },
    onSuccess: ({ uploadFailed }) => {
      if (uploadFailed) {
        toast.warn('The entry was saved without its document — attach one from its card.');
      } else {
        toast.success(isEdit ? `${section.label} updated` : `${section.label} added`);
      }
      invalidate();
      onClose();
    },
    onError: (error: Error) => {
      fieldErrorsFrom(error).forEach(([name, message]) => setError(name, { message }));
      handleApiError(error, {
        messagePrefix: isEdit ? `Error updating ${section.label}` : `Error adding ${section.label}`,
      });
    },
  });
  const isPending = saveMutation.isPending;

  const onSubmit = handleSubmit((values) => {
    const missing = requiredPickError(section, values);
    if (missing) {
      setError(missing[0], { message: missing[1] });
      return;
    }
    saveMutation.mutate(values);
  });

  const showFile = !row?.document;
  const fileIs =
    kind === 'immunization' && watch('exempt')
      ? 'The exemption letter for this'
      : 'The image of this';

  return (
    <Dialog open={open} onClose={onClose} size="xl">
      <DialogTitle className="tw:!text-2xl tw:!text-brand-green-0 tw:!mt-0 tw:!pt-0">
        {isEdit ? `Edit ${row?.title ?? section.label}` : `Add ${section.label}`}
      </DialogTitle>
      <form onSubmit={onSubmit} data-testid="credential-form">
        <DialogBody>
          <Fieldset>
            <div className="tw:grid tw:grid-cols-1 tw:sm:grid-cols-2 tw:gap-3">
              <SectionFields form={form} isPending={isPending} kind={kind} />
              <Field>
                <Label>Issue Date</Label>
                <Controller
                  name="issue_date"
                  control={control}
                  render={({ field }) => (
                    <ReactDatePicker
                      className="datepicker-input"
                      wrapperClassName="datepicker-wrapper"
                      selected={field.value ?? null}
                      onChange={(date) => field.onChange(date)}
                      placeholderText="Select issue date"
                      dateFormat="MM/dd/yyyy"
                      disabled={isPending}
                    />
                  )}
                />
                {formState.errors.issue_date && (
                  <FieldValidationErrorMessage message={formState.errors.issue_date.message} />
                )}
              </Field>
              <Field>
                <Label>Expiration Date</Label>
                <Controller
                  name="expiration_date"
                  control={control}
                  render={({ field }) => (
                    <ReactDatePicker
                      className="datepicker-input"
                      wrapperClassName="datepicker-wrapper"
                      selected={field.value ?? null}
                      onChange={(date) => field.onChange(date)}
                      placeholderText="Select expiration date"
                      dateFormat="MM/dd/yyyy"
                      disabled={isPending}
                    />
                  )}
                />
                {formState.errors.expiration_date && (
                  <FieldValidationErrorMessage message={formState.errors.expiration_date.message} />
                )}
              </Field>
            </div>
            {showFile && (
              <Field className="tw:mt-3">
                <Label>{isEdit ? 'Document' : 'Document (optional)'}</Label>
                <input
                  ref={fileInputRef}
                  type="file"
                  accept="application/pdf,image/*"
                  disabled={isPending}
                  onChange={(event) => {
                    setValue('file', event.target.files?.[0] ?? undefined, {
                      shouldValidate: true,
                    });
                  }}
                  className={FILE_INPUT_CLASSES}
                  data-testid="credential-file"
                />
                <Description>
                  {fileIs} {section.label.toLowerCase()}. It is stored with the clinician&apos;s
                  documents and listed on the Documents tab too.
                </Description>
                {formState.errors.file && (
                  <FieldValidationErrorMessage message={formState.errors.file.message} />
                )}
              </Field>
            )}
          </Fieldset>
        </DialogBody>
        <DialogActions>
          <Button type="button" color="zinc-outline" onClick={onClose} disabled={isPending}>
            Cancel
          </Button>
          <Button type="submit" color="brandgreen1" disabled={isPending}>
            {isPending ? 'Saving…' : isEdit ? 'Save' : `Add ${section.label}`}
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
}
